import React, { useState, useCallback } from "react";
import {
  Pressable,
  ScrollView,
  TextInput,
  View,
  StyleSheet,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import { useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";
import { requestPhoneChange } from "@services/phoneChangeApi";
import { fontFamily } from "@utils/styles/fonts";

const textPrimary = color.Gray20;
const textMuted = color.color_808080;
const borderDefault = color.color_D9D9D9;
const placeholderColor = color.disableFont;
const dividerColor = color.lineColor;
const shadowColor = color.black;

const PhoneChangeRequest: React.FC = () => {
  const navigation = useNavigation();
  const {
    db_name = "",
    db_phone_number = "",
    cp_db_id = "",
  } = useSelector(
    ({
      auth,
    }: {
      auth: {
        user: { db_name?: string; db_phone_number?: string; cp_db_id?: string };
      };
    }) => auth.user,
  );

  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidPhone =
    newPhoneNumber.length === 10 && !isNaN(Number(newPhoneNumber));

  const onChangeText = useCallback((text: string) => {
    const digits = text.replace(/[^\d]/g, "").slice(0, 10);
    setNewPhoneNumber(digits);
  }, []);

  const onSubmit = useCallback(async () => {
    if (isSubmitting || !isValidPhone) return;

    setIsSubmitting(true);
    try {
      await requestPhoneChange(cp_db_id, newPhoneNumber);
      toast.showLongToast("Phone change request submitted successfully.");
      navigation.goBack();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.showLongToast(
        axiosErr?.response?.data?.message ||
          "Failed to submit request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isValidPhone, cp_db_id, newPhoneNumber, navigation]);

  return (
    <>
      <View style={styles.container}>
        <HeaderWithBackAndTitle
          headertitle="Change Phone Number"
          navigation={navigation}
          hideShadow
        />

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Name — prefilled, disabled */}
          <View style={[styles.inputContainer, styles.inputDisabled]}>
            {db_name ? <Text style={styles.inputLabel}>Name</Text> : null}
            <TextInput
              value={db_name}
              style={[styles.inputText, styles.inputTextDisabled]}
              editable={false}
              placeholder="Name"
              placeholderTextColor={placeholderColor}
              accessibilityLabel="Name"
            />
          </View>

          {/* Old Phone Number — disabled */}
          <View style={[styles.inputContainer, styles.inputDisabled]}>
            {db_phone_number ? (
              <Text style={styles.inputLabel}>Old Mobile Number</Text>
            ) : null}
            <TextInput
              value={db_phone_number}
              style={[styles.inputText, styles.inputTextDisabled]}
              editable={false}
              placeholder="Old Mobile Number"
              placeholderTextColor={placeholderColor}
              accessibilityLabel="Old mobile number"
            />
          </View>

          {/* New Phone Number */}
          <View
            style={[
              styles.inputContainer,
              isFocused && styles.inputContainerFocused,
            ]}
          >
            {newPhoneNumber ? (
              <Text style={styles.inputLabel}>New Mobile Number</Text>
            ) : null}
            <TextInput
              value={newPhoneNumber}
              style={styles.inputText}
              placeholder="New Mobile Number"
              placeholderTextColor={placeholderColor}
              keyboardType="number-pad"
              maxLength={10}
              onChangeText={onChangeText}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              accessibilityLabel="New mobile number"
            />
          </View>
        </ScrollView>

        <View style={styles.bottomBar}>
          <Pressable
            style={[
              styles.submitButton,
              !isValidPhone && styles.submitButtonDisabled,
            ]}
            disabled={!isValidPhone || isSubmitting}
            onPress={onSubmit}
            accessibilityRole="button"
            accessibilityLabel="Send Number Change Request"
          >
            <Text variant="title_16" style={styles.submitButtonText}>
              Send Number Change Request
            </Text>
          </Pressable>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: color.white },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 108,
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 10,
    marginBottom: 16,
    backgroundColor: color.white,
    minHeight: 56,
    justifyContent: "center",
  },
  inputContainerFocused: { borderColor: color.primary },
  inputDisabled: { backgroundColor: color.lightGray },
  inputLabel: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.primary,
    marginBottom: 2,
  },
  inputText: {
    color: textPrimary,
    padding: 0,
    margin: 0,
    fontFamily: fontFamily.regular,
  },
  inputTextDisabled: { color: textMuted },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
  submitButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  submitButtonDisabled: { backgroundColor: color.lighterBlack },
  submitButtonText: { color: color.white },
});

export default PhoneChangeRequest;
